/**
 * Find highlights in essay text based on feedback data
 * @param {string} essayText - The student's essay text
 * @param {Array} strengths - Array of strength feedback items
 * @param {Array} improvements - Array of improvement feedback items
 * @returns {Array} Array of highlight objects with positions
 */
export function findHighlights(essayText, strengths, improvements) {
    if (!essayText) return [];

    const highlights = [];

    // Create normalized text with position mapping once
    const { normalizedText, positionMap } = normalizeWithMapping(essayText);

    // Process strengths (blue highlights)
    strengths?.forEach((s, idx) => {
        if (s.representative_sentence) {
            const match = findTextMatch(normalizedText, positionMap, s.representative_sentence);
            if (match) {
                highlights.push({
                    id: `strength-${idx}`,
                    type: 'strength',
                    startIndex: match.start,
                    endIndex: match.end,
                    data: { ...s, id: `strength-${idx}` },
                });
            }
        }
    });

    // Process improvements (red highlights)
    improvements?.forEach((i, idx) => {
        if (i.representative_sentence) {
            const match = findTextMatch(normalizedText, positionMap, i.representative_sentence);
            if (match) {
                highlights.push({
                    id: `improvement-${idx}`,
                    type: 'improvement',
                    startIndex: match.start,
                    endIndex: match.end,
                    data: { ...i, id: `improvement-${idx}` },
                });
            }
        }
    });

    // Resolve overlapping highlights
    return resolveOverlaps(highlights);
}

/**
 * Normalize special characters (smart quotes, dashes, etc.) to standard ASCII
 * @param {string} text - Text to normalize
 * @returns {string} Normalized text
 */
function normalizeSpecialChars(text) {
    return text
        // Smart quotes to straight quotes
        .replace(/[\u2018\u2019\u0060\u00B4]/g, "'")  // ' ' ` ´ → '
        .replace(/[\u201C\u201D\u00AB\u00BB]/g, '"')  // " " « » → "
        // Dashes to hyphen
        .replace(/[\u2013\u2014\u2212]/g, '-')        // – — − → -
        // Ellipsis
        .replace(/\u2026/g, '...')                     // … → ...
        // Non-breaking space and other spaces
        .replace(/[\u00A0\u2002\u2003\u2009]/g, ' '); // NBSP and other space variants → space
}

/**
 * Normalize text while creating a mapping from normalized indices to original indices
 * This allows us to find matches in normalized text but return correct original positions
 * @param {string} text - Original text
 * @returns {Object} { normalizedText, positionMap }
 */
function normalizeWithMapping(text) {
    // First normalize special characters
    const charNormalized = normalizeSpecialChars(text);

    const normalizedChars = [];
    const positionMap = []; // positionMap[normalizedIndex] = originalIndex

    let inWhitespace = false;

    for (let i = 0; i < charNormalized.length; i++) {
        const char = charNormalized[i];
        if (/\s/.test(char)) {
            if (!inWhitespace) {
                // First whitespace character - add a single space
                normalizedChars.push(' ');
                positionMap.push(i);
                inWhitespace = true;
            }
            // Skip additional consecutive whitespace characters
        } else {
            normalizedChars.push(char);
            positionMap.push(i);
            inWhitespace = false;
        }
    }

    return {
        normalizedText: normalizedChars.join(''),
        positionMap: positionMap,
    };
}

/**
 * Find text match in normalized essay and return original positions
 * @param {string} normalizedEssay - The normalized essay text
 * @param {Array} positionMap - Mapping from normalized to original indices
 * @param {string} searchText - The text to find
 * @returns {Object|null} Match object with start and end indices in ORIGINAL text, or null if not found
 */
function findTextMatch(normalizedEssay, positionMap, searchText) {
    if (!normalizedEssay || !searchText) return null;

    // Normalize search text the same way (special chars + whitespace)
    // Also strip leading/trailing quotes that AI sometimes adds around representative sentences
    const normalizedSearch = normalizeSpecialChars(searchText)
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^["']+|["']+$/g, '') // Remove leading/trailing quotes
        .trim();

    // Try exact match first (case insensitive)
    const lowerEssay = normalizedEssay.toLowerCase();
    const lowerSearch = normalizedSearch.toLowerCase();

    let index = lowerEssay.indexOf(lowerSearch);
    if (index !== -1) {
        return mapToOriginalPositions(index, normalizedSearch.length, positionMap);
    }

    // Try to find the longest matching substring
    // Start with full sentence, then try progressively shorter from both ends
    const words = normalizedSearch.split(' ');

    if (words.length >= 3) {
        let bestMatch = null;
        let bestLength = 0;

        // Try different start positions
        for (let start = 0; start < words.length; start++) {
            // Try different end positions (from longest to shortest)
            for (let end = words.length; end > start + 2; end--) {
                const partialWords = words.slice(start, end);
                const partialSearch = partialWords.join(' ').toLowerCase();

                index = lowerEssay.indexOf(partialSearch);
                if (index !== -1) {
                    // Found a match - check if it's longer than our best
                    if (partialSearch.length > bestLength) {
                        bestMatch = { index, length: partialSearch.length };
                        bestLength = partialSearch.length;
                    }
                    // Since we're going from longest to shortest for this start,
                    // no need to try shorter versions
                    break;
                }
            }
        }

        if (bestMatch) {
            return mapToOriginalPositions(bestMatch.index, bestMatch.length, positionMap);
        }
    }

    // Fallback: fuzzy match using sliding window
    return fuzzyMatch(normalizedEssay, positionMap, normalizedSearch);
}

/**
 * Map normalized indices back to original text positions
 * @param {number} normalizedStart - Start index in normalized text
 * @param {number} matchLength - Length of match in normalized text
 * @param {Array} positionMap - Mapping from normalized to original indices
 * @returns {Object} { start, end } in original text
 */
function mapToOriginalPositions(normalizedStart, matchLength, positionMap) {
    const normalizedEnd = normalizedStart + matchLength - 1; // Last character index (inclusive)

    const originalStart = positionMap[normalizedStart];
    // For end, we want one past the last matched character in original
    // positionMap[normalizedEnd] gives us the original index of the last matched char
    // We add 1 to make it exclusive (standard slice behavior)
    const originalEnd = positionMap[normalizedEnd] + 1;

    return {
        start: originalStart,
        end: originalEnd,
    };
}

/**
 * Fuzzy match using similarity scoring
 * @param {string} normalizedEssay - The normalized essay text
 * @param {Array} positionMap - Mapping from normalized to original indices
 * @param {string} normalizedSearch - The normalized text to find
 * @returns {Object|null} Match object or null
 */
function fuzzyMatch(normalizedEssay, positionMap, normalizedSearch) {
    if (!normalizedEssay || !normalizedSearch || normalizedSearch.length < 10) return null;

    const lowerEssay = normalizedEssay.toLowerCase();
    const lowerSearch = normalizedSearch.toLowerCase();

    // Use a sliding window approach
    const windowSize = lowerSearch.length;
    let bestMatch = { index: -1, score: 0 };

    for (let i = 0; i <= lowerEssay.length - windowSize; i++) {
        const window = lowerEssay.slice(i, i + windowSize);
        const score = calculateSimilarity(window, lowerSearch);

        if (score > bestMatch.score && score > 0.8) { // 80% similarity threshold
            bestMatch = { index: i, score };
        }
    }

    if (bestMatch.index !== -1) {
        return mapToOriginalPositions(bestMatch.index, windowSize, positionMap);
    }

    return null;
}

/**
 * Calculate similarity between two strings (simple character-based)
 * @param {string} str1 - First string
 * @param {string} str2 - Second string
 * @returns {number} Similarity score between 0 and 1
 */
function calculateSimilarity(str1, str2) {
    if (str1 === str2) return 1;
    if (!str1 || !str2) return 0;

    const longer = str1.length > str2.length ? str1 : str2;
    const shorter = str1.length > str2.length ? str2 : str1;

    if (longer.length === 0) return 1;

    // Simple character matching
    let matches = 0;
    for (let i = 0; i < shorter.length; i++) {
        if (shorter[i] === longer[i]) matches++;
    }

    return matches / longer.length;
}

/**
 * Resolve overlapping highlights - keep both but adjust boundaries
 * @param {Array} highlights - Array of highlight objects
 * @returns {Array} Resolved highlights without overlaps
 */
function resolveOverlaps(highlights) {
    if (highlights.length <= 1) return highlights;

    // Sort by start index
    const sorted = [...highlights].sort((a, b) => a.startIndex - b.startIndex);

    const resolved = [];
    let prev = null;

    for (const highlight of sorted) {
        if (!prev) {
            resolved.push(highlight);
            prev = highlight;
            continue;
        }

        // Check for overlap
        if (highlight.startIndex < prev.endIndex) {
            // Overlapping - truncate the previous one or skip if same type
            if (prev.type === highlight.type) {
                // Same type - merge by extending prev
                prev.endIndex = Math.max(prev.endIndex, highlight.endIndex);
            } else {
                // Different types - truncate prev and add current
                if (highlight.startIndex > prev.startIndex) {
                    // There's some non-overlapping part in prev
                    prev.endIndex = highlight.startIndex;
                }
                resolved.push(highlight);
                prev = highlight;
            }
        } else {
            resolved.push(highlight);
            prev = highlight;
        }
    }

    return resolved;
}
